import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { propertiesAPI, ownersAPI } from '../services/api';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const initialFormData = {
  owner: '',
  propertyName: '',
  acquisitionType: '',
  acquisitionTypeOther: '',
  propertyType: '',
  availableArea: '',
  totalArea: '',
  availabilityStatus: 'Disponível',
  expectedRent: '',
  documents: {
    matricula: false,
    escritura: false,
    iptu: false,
    certidaoNegativa: false,
    certidaoOnus: false,
    habiteseART: false,
    laudoAvaliacao: false,
    plantaBaixa: false,
    memoralDescritivo: false,
    licencaAmbiental: false,
    outorgaAnatel: false,
    procuracaoRepresentante: false
  },
  infrastructure: {
    hasElectricity: false,
    hasWater: false,
    hasFiber: false,
    hasAccess24h: false,
    hasSecurity: false,
    hasParking: false
  },
  address: {
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: ''
  },
  location: {
    type: 'Point',
    coordinates: [-46.6333, -23.5505] // São Paulo default
  },
  notes: ''
};

const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const acquisitionTypes = [
  'Compra e Venda',
  'Herança',
  'Doação',
  'Usucapião',
  'Permuta',
  'Financiamento',
  'Arrematação em Leilão',
  'Dação em Pagamento',
  'Cessão de Direitos',
  'Incorporação Imobiliária',
  'Adjudicação',
  'Desapropriação',
  'Outro'
];

const documentLabels = {
  matricula: 'Matrícula do Imóvel',
  escritura: 'Escritura Pública',
  iptu: 'IPTU',
  certidaoNegativa: 'Certidão Negativa de Débitos',
  certidaoOnus: 'Certidão de Ônus Reais',
  habiteseART: 'Habite-se / ART',
  laudoAvaliacao: 'Laudo de Avaliação',
  plantaBaixa: 'Planta Baixa',
  memoralDescritivo: 'Memorial Descritivo',
  licencaAmbiental: 'Licença Ambiental',
  outorgaAnatel: 'Outorga ANATEL (se aplicável)',
  procuracaoRepresentante: 'Procuração do Representante'
};

const infrastructureLabels = {
  hasElectricity: 'Energia Elétrica',
  hasWater: 'Água',
  hasFiber: 'Fibra Óptica',
  hasAccess24h: 'Acesso 24 horas',
  hasSecurity: 'Vigilância/Segurança',
  hasParking: 'Estacionamento'
};

// Map click handler component
function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect([e.latlng.lng, e.latlng.lat]);
    },
  });
  return null;
}

function PropertyForm() {
  const [formData, setFormData] = useState(initialFormData);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  useEffect(() => {
    loadOwners();
    if (isEditing) {
      loadProperty();
    }
  }, [id]);

  const loadOwners = async () => {
    try {
      const response = await ownersAPI.getForSelect();
      setOwners(response.data);
    } catch (error) {
      toast.error('Erro ao carregar proprietários');
    }
  };

  const loadProperty = async () => {
    setLoadingData(true);
    try {
      const response = await propertiesAPI.getById(id);
      const property = response.data;
      setFormData({
        ...property,
        owner: property.owner?._id || property.owner
      });
    } catch (error) {
      toast.error('Erro ao carregar dados do imóvel');
      navigate('/imoveis');
    } finally {
      setLoadingData(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleLocationSelect = (coordinates) => {
    setFormData(prev => ({
      ...prev,
      location: {
        type: 'Point',
        coordinates: coordinates
      }
    }));
    toast.info('Localização marcada no mapa!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.owner) {
      toast.error('Selecione um proprietário');
      return;
    }
    
    setLoading(true);

    try {
      const dataToSend = {
        ...formData,
        totalArea: formData.totalArea ? Number(formData.totalArea) : undefined,
        expectedRent: formData.expectedRent ? Number(formData.expectedRent) : undefined
      };

      if (isEditing) {
        await propertiesAPI.update(id, dataToSend);
        toast.success('Imóvel atualizado com sucesso!');
      } else {
        await propertiesAPI.create(dataToSend);
        toast.success('Imóvel cadastrado com sucesso! E-mail enviado para lista de distribuição.');
      }
      navigate('/imoveis');
    } catch (error) {
      const message = error.response?.data?.message || 
                      error.response?.data?.errors?.[0]?.msg ||
                      'Erro ao salvar imóvel';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="loading-spinner">
        <div className="spinner"></div>
      </div>
    );
  }

  const mapCenter = formData.location?.coordinates 
    ? [formData.location.coordinates[1], formData.location.coordinates[0]]
    : [-23.5505, -46.6333];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">
            {isEditing ? 'Editar Imóvel' : 'Novo Imóvel'}
          </h1>
          <p className="page-subtitle">
            {isEditing ? 'Atualize os dados do imóvel' : 'Cadastre um novo imóvel para locação de ERB'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card">
          <div className="card-body">
            {/* Proprietário */}
            <div className="form-section">
              <h3 className="form-section-title">👤 Proprietário</h3>
              <div className="form-group">
                <label className="form-label required">Selecione o Proprietário</label>
                <select
                  name="owner"
                  className="form-select"
                  value={formData.owner}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecione um proprietário</option>
                  {owners.map(owner => (
                    <option key={owner._id} value={owner._id}>
                      {owner.name} - {owner.type === 'Pessoa Física' ? owner.cpf : owner.cnpj} ({owner.email})
                    </option>
                  ))}
                </select>
                {owners.length === 0 && (
                  <p style={{ marginTop: '8px', fontSize: '13px', color: 'var(--warning)' }}>
                    ⚠️ Nenhum proprietário cadastrado. <a href="/proprietarios/novo">Cadastre um proprietário primeiro</a>.
                  </p>
                )}
              </div>
            </div>

            {/* Identificação do Imóvel */}
            <div className="form-section">
              <h3 className="form-section-title">🏢 Identificação do Imóvel</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Nome/Identificação</label>
                  <input
                    type="text"
                    name="propertyName"
                    className="form-input"
                    placeholder="Ex: Terreno Centro, Cobertura Ed. Central"
                    value={formData.propertyName}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Tipo de Imóvel</label>
                  <select
                    name="propertyType"
                    className="form-select"
                    value={formData.propertyType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="Terreno">Terreno</option>
                    <option value="Cobertura">Cobertura</option>
                    <option value="Laje">Laje</option>
                    <option value="Telhado">Telhado</option>
                    <option value="Torre Existente">Torre Existente</option>
                    <option value="Outro">Outro</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Tipo de Aquisição</label>
                  <select
                    name="acquisitionType"
                    className="form-select"
                    value={formData.acquisitionType}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    {acquisitionTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                {formData.acquisitionType === 'Outro' && (
                  <div className="form-group">
                    <label className="form-label">Especifique</label>
                    <input
                      type="text"
                      name="acquisitionTypeOther"
                      className="form-input"
                      placeholder="Descreva o tipo de aquisição"
                      value={formData.acquisitionTypeOther}
                      onChange={handleChange}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Área e Disponibilidade */}
            <div className="form-section">
              <h3 className="form-section-title">📐 Área e Disponibilidade</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Área Disponível para Locação</label>
                  <select
                    name="availableArea"
                    className="form-select"
                    value={formData.availableArea}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    <option value="10x10">10x10 (100m²)</option>
                    <option value="10x15">10x15 (150m²)</option>
                    <option value="15x15">15x15 (225m²)</option>
                    <option value="15x20">15x20 (300m²)</option>
                    <option value="20x20">20x20 (400m²)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Área Total do Terreno (m²)</label>
                  <input
                    type="number"
                    name="totalArea"
                    className="form-input"
                    placeholder="Ex: 500"
                    value={formData.totalArea}
                    onChange={handleChange}
                    min="0"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Status do Cadastro</label>
                  <select
                    name="availabilityStatus"
                    className="form-select"
                    value={formData.availabilityStatus}
                    onChange={handleChange}
                  >
                    <option value="Disponível">Disponível</option>
                    <option value="Em Negociação">Em Negociação</option>
                    <option value="Em Assinatura do Contrato">Em Assinatura do Contrato</option>
                    <option value="Contrato Assinado">Contrato Assinado</option>
                    <option value="Em Licenciamento">Em Licenciamento</option>
                    <option value="Licença Emitida">Licença Emitida</option>
                    <option value="Reservado">Reservado</option>
                    <option value="Locado">Locado</option>
                    <option value="Indisponível">Indisponível</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Valor Pretendido (R$/mês)</label>
                  <input
                    type="number"
                    name="expectedRent"
                    className="form-input"
                    placeholder="Ex: 5000"
                    value={formData.expectedRent}
                    onChange={handleChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Documentos */}
            <div className="form-section">
              <h3 className="form-section-title">📄 Documentos Disponíveis</h3>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginBottom: '16px' }}>
                Marque os documentos que o proprietário possui disponíveis:
              </p>
              <div className="checkbox-grid">
                {Object.entries(documentLabels).map(([key, label]) => (
                  <label key={key} className="form-checkbox">
                    <input
                      type="checkbox"
                      name={`documents.${key}`}
                      checked={formData.documents?.[key] || false}
                      onChange={handleChange}
                    />
                    <span className="form-checkbox-label">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Infraestrutura */}
            <div className="form-section">
              <h3 className="form-section-title">⚡ Infraestrutura Existente</h3>
              <div className="checkbox-grid">
                {Object.entries(infrastructureLabels).map(([key, label]) => (
                  <label key={key} className="form-checkbox">
                    <input
                      type="checkbox"
                      name={`infrastructure.${key}`}
                      checked={formData.infrastructure?.[key] || false}
                      onChange={handleChange}
                    />
                    <span className="form-checkbox-label">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Endereço */}
            <div className="form-section">
              <h3 className="form-section-title">📍 Endereço do Imóvel</h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">CEP</label>
                  <input
                    type="text"
                    name="address.zipCode"
                    className="form-input"
                    placeholder="00000-000"
                    value={formData.address?.zipCode || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label required">Logradouro</label>
                  <input
                    type="text"
                    name="address.street"
                    className="form-input"
                    placeholder="Rua, Avenida, etc."
                    value={formData.address?.street || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Número</label>
                  <input
                    type="text"
                    name="address.number"
                    className="form-input"
                    placeholder="Nº"
                    value={formData.address?.number || ''}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Complemento</label>
                  <input
                    type="text"
                    name="address.complement"
                    className="form-input"
                    placeholder="Lote, Quadra, etc."
                    value={formData.address?.complement || ''}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label required">Bairro</label>
                  <input
                    type="text"
                    name="address.neighborhood"
                    className="form-input"
                    placeholder="Bairro"
                    value={formData.address?.neighborhood || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Cidade</label>
                  <input
                    type="text"
                    name="address.city"
                    className="form-input"
                    placeholder="Cidade"
                    value={formData.address?.city || ''}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label required">Estado</label>
                  <select
                    name="address.state"
                    className="form-select"
                    value={formData.address?.state || ''}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Selecione</option>
                    {states.map(state => (
                      <option key={state} value={state}>{state}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Mapa */}
            <div className="form-section">
              <h3 className="form-section-title">🗺️ Localização no Mapa</h3>
              <p style={{ fontSize: '13px', color: 'var(--gray-600)', marginBottom: '16px' }}>
                Clique no mapa para marcar o ponto central da área a ser locada:
              </p>
              <div className="map-container">
                <MapContainer 
                  center={mapCenter} 
                  zoom={13} 
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler onLocationSelect={handleLocationSelect} />
                  {formData.location?.coordinates && (
                    <Marker position={[formData.location.coordinates[1], formData.location.coordinates[0]]} />
                  )}
                </MapContainer>
              </div>
              <p className="map-instructions">
                📍 Coordenadas: {formData.location?.coordinates?.[1]?.toFixed(6)}, {formData.location?.coordinates?.[0]?.toFixed(6)}
              </p>
            </div>

            {/* Observações */}
            <div className="form-section">
              <h3 className="form-section-title">📝 Observações</h3>
              <div className="form-group">
                <textarea
                  name="notes"
                  className="form-textarea"
                  placeholder="Observações adicionais sobre o imóvel, condições especiais, etc..."
                  value={formData.notes}
                  onChange={handleChange}
                  rows={4}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/imoveis')}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Salvando...' : (isEditing ? 'Salvar Alterações' : 'Cadastrar Imóvel')}
          </button>
        </div>
      </form>
    </div>
  );
}

export default PropertyForm;
